import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";
import { getCurrentCustomer } from "../../utils/auth";
import { useSettings } from "../../context/SettingsContext";
import { useTranslation } from "react-i18next";


const MyOrders = () => {
  const { t, i18n } = useTranslation();
  const { currencySymbol } = useSettings();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getReturnStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      reviewing: "bg-blue-100 text-blue-800 border-blue-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      received: "bg-purple-100 text-purple-800 border-purple-200",
      refunded: "bg-emerald-100 text-emerald-800 border-emerald-200",
      completed: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const customer = getCurrentCustomer();
    const fetchOrders = async () => {
      if (!customer?.id) {
        setError(t("myOrders.loginError", "Please login to view your orders."));
        setLoading(false);
        return;
      }

      try {
        const res = await api.get("/customer/my-orders", {
          params: { customer_id: customer.id },
        });
        setOrders(res?.data?.data?.data || []);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) return <div className="text-gray-600">{t("myOrders.loading", "Loading orders...")}</div>;

  return (
    <div className="overlow-hidden">
      <h2 className="text-xl text-black font-semibold mb-5">{t("myOrders.title", "My Orders")}</h2>
      {error ? (
        <p className="text-red-500 text-sm">{error}</p>
      ) : orders.length === 0 ? (
        <p className="text-gray-500 text-sm">{t("myOrders.noOrders", "No orders found yet.")}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-100 rounded-md">
          <table className="table-auto min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tableOrder", "Order")}</th>
                <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tableDate", "Date")}</th>
                <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tableStatus", "Status")}</th>
                <th className="text-left text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tablePayment", "Payment")}</th>
                <th className="text-right text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tableTotal", "Total")}</th>
                <th className="text-right text-xs font-semibold px-6 py-2 text-gray-700 uppercase">{t("myOrders.tableAction", "Actions")}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="px-6 py-3 text-sm text-gray-700">#{order.order_number || order.id}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{new Date(order.created_at).toLocaleDateString(i18n.language)}</td>
                  <td className="px-6 py-3 text-sm text-gray-700 capitalize">{t(`status.${order.status?.toLowerCase()}`, { defaultValue: order.status })}</td>
                  <td className="px-6 py-3 text-sm text-gray-700 capitalize">{t(`status.${order.payment_status?.toLowerCase()}`, { defaultValue: order.payment_status })}</td>
                  <td className="px-6 py-3 text-sm text-right font-medium text-gray-800">{currencySymbol} {Number(order.total_amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-3 text-sm text-right flex items-center justify-end gap-2.5">
                    {order.return_requests && order.return_requests.length > 0 && (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border capitalize ${getReturnStatusColor(order.return_requests[0].status)}`}>
                        {t('myOrders.returnStatus', 'Return')}: {order.return_requests[0].status}
                      </span>
                    )}
                    {order.status?.toLowerCase() === 'delivered' && (
                      <Link
                        to={`/user/return-request/${order.id}`}
                        className="text-emerald-600 hover:text-emerald-700 font-semibold text-xs border border-emerald-200 hover:bg-emerald-50 px-2.5 py-1 rounded transition duration-150"
                      >
                        {order.return_requests && order.return_requests.length > 0
                          ? t('myOrders.viewReturn', 'View Return')
                          : t('myOrders.requestReturn', 'Request Return')}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MyOrders